import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    marketsService,
    type CreateMarketInput,
    type PlaceBetInput,
} from '../services/markets.service';

const invalidateEverythingMoneyTouches = (queryClient: ReturnType<typeof useQueryClient>) => {
    void queryClient.invalidateQueries({ queryKey: ['markets'] });
    // A stake moves a balance and a balance moves the leaderboard.
    void queryClient.invalidateQueries({ queryKey: ['hangouts'] });
};

export const useMarkets = () =>
    useQuery({
        queryKey: ['markets'],
        queryFn: () => marketsService.list(),
        // Odds shift as people pile in, and watching that happen is most of the
        // fun. Five seconds is invisible to a dozen users and removes an entire
        // class of connection-state bugs.
        refetchInterval: 5_000,
        staleTime: 0,
    });

export const useMarket = (id: number | undefined) =>
    useQuery({
        queryKey: ['markets', id],
        queryFn: () => marketsService.get(id as number),
        enabled: id !== undefined,
        refetchInterval: 5_000,
        staleTime: 0,
    });

export const useCreateMarket = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: CreateMarketInput) => marketsService.create(input),
        onSuccess: () => invalidateEverythingMoneyTouches(queryClient),
    });
};

export const usePlaceBet = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: PlaceBetInput) => marketsService.bet(input),
        onSuccess: () => invalidateEverythingMoneyTouches(queryClient),
        // The server collapses repeats by idempotency key, so retrying a failed
        // submission cannot place a second bet.
        retry: 2,
    });
};

export const useEnterFreeForAll = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (vars: { marketId: number; idempotencyKey: string }) =>
            marketsService.enter(vars.marketId, vars.idempotencyKey),
        onSuccess: () => invalidateEverythingMoneyTouches(queryClient),
        retry: 2,
    });
};

export const useResolveMarket = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (vars: {
            marketId: number;
            outcomeId?: number;
            resolvedValue?: number;
        }) => marketsService.resolve(vars.marketId, vars),
        onSuccess: () => invalidateEverythingMoneyTouches(queryClient),
    });
};



export const useDisputes = (marketId: number | undefined) =>
    useQuery({
        queryKey: ['markets', marketId, 'disputes'],
        queryFn: () => marketsService.disputes(marketId as number),
        enabled: marketId !== undefined,
        refetchInterval: 5_000,
    });

export const useRaiseDispute = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (vars: { marketId: number; reason: string }) =>
            marketsService.dispute(vars.marketId, vars.reason),
        onSuccess: (_result, vars) => {
            void queryClient.invalidateQueries({ queryKey: ['markets', vars.marketId] });
        },
    });
};

export const useVoteOnDispute = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (vars: { disputeId: number; agrees: boolean; marketId: number }) =>
            marketsService.voteOnDispute(vars.disputeId, vars.agrees),
        onSuccess: (_result, vars) => {
            // A carried vote voids the market, which moves balances.
            void queryClient.invalidateQueries({ queryKey: ['markets', vars.marketId] });
            void queryClient.invalidateQueries({ queryKey: ['markets'] });
            void queryClient.invalidateQueries({ queryKey: ['hangouts'] });
        },
    });
};
